/**
 * Validacion zod de FichaCata. La consume el admin (que puede value-importar el
 * paquete) para validar la ficha de referencia antes de crear/editar. La API valida
 * estructuralmente por su cuenta (no puede value-importar el paquete en runtime).
 */

import { z } from 'zod'

const descriptorFamilia = z
  .object({
    seleccionados: z.array(z.string()),
    otros: z.string().optional(),
  })
  .passthrough()

// Forma de dos argumentos: compatible con zod v3 (admin/api) y v4 (app).
const seleccionDescriptores = z.record(z.string(), z.record(z.string(), descriptorFamilia))

const defectos = z
  .object({
    seleccion: z.array(z.string()),
    otro: z.string(),
  })
  .passthrough()

const apariencia = z
  .object({
    colorPrincipal: z.string(),
    intensidad: z.string(),
    colorInterior: z.string(),
    variacionBorde: z.string(),
    viscosidad: z.string(),
    otrasObservaciones: z.string(),
    claridadScore: z.number(),
    brilloScore: z.number(),
    viscosidadScore: z.number(),
  })
  .passthrough()

const nariz = z
  .object({
    limpioDefectuoso: z.string(),
    intensidad: z.string(),
    sensacionAlcohol: z.string(),
    influenciaRoble: z.string(),
    nivelMadurez: z.string(),
    defectos,
    aromas: seleccionDescriptores,
    condicionFruta: z.number(),
  })
  .passthrough()

const gusto = z
  .object({
    limpioDefectuoso: z.string(),
    secoDulce: z.string(),
    co2: z.string(),
    cuerpo: z.string(),
    intensidad: z.string(),
    confirmacionRoble: z.string(),
    nivelMadurez: z.string(),
    complejidad: z.string(),
    defectos,
    co2Intensidad: z.string(),
    acidezEscala: z.string(),
    acidezComplementario: z.array(z.string()),
    acidezComplementarioOtro: z.string(),
    alcoholEscala: z.string(),
    alcoholComplementario: z.array(z.string()),
    alcoholComplementarioOtro: z.string(),
    cuerpoComplementario: z.array(z.string()),
    cuerpoComplementarioOtro: z.string(),
    taninosNivel: z.string(),
    taninosComplementario: z.array(z.string()),
    taninosComplementarioOtro: z.string(),
    persistenciaEscala: z.string(),
    sabores: seleccionDescriptores,
    condicionFruta: z.number(),
  })
  .passthrough()

const conclusiones = z
  .object({
    climaFrioCalido: z.string(),
    apelacion: z.string(),
    region: z.string(),
    pais: z.string(),
    rangoEdad: z.string(),
    anada: z.string(),
    etiquetaVino: z.string(),
    productor: z.string(),
    calidad: z.string(),
    potencialGuarda: z.string(),
    temperatura: z.string(),
    seleccionCopas: z.string(),
    descripcionPlatoRecomendado: z.string(),
    razonMaridaje: z.string(),
    vinificacionCategoria: z.string(),
    vinificacionEstilo: z.array(z.string()),
    vinificacionOtro: z.string(),
    climaTipo: z.string(),
    variedadUvas: z.array(z.string()),
    variedadUvaOtros: z.string(),
    servicio: z.string(),
    potencialGuardaOtro: z.string(),
  })
  .passthrough()

/** Contenido evaluable de la ficha (sin metadatos de sync/persistencia). */
export const fichaContentSchema = z
  .object({
    vino: z.string(),
    cosecha: z.string(),
    origen: z.string(),
    alcPorcentaje: z.string(),
    apariencia,
    nariz,
    gusto,
    conclusiones,
  })
  .passthrough()

export type FichaContentInput = z.infer<typeof fichaContentSchema>
